/**
 * Tricount compartido (puro), visto desde quien mira («me»): el saldo, desde cuándo no se salda, cómo ha ido creciendo,
 * el gasto en común de un mes (quién pagó qué y tu parte) y las filas con tu parte y si están en tu libro.
 * Reparto al 50 %: cada gasto que pagas tú hace que el otro te deba la mitad, y al revés.
 * El saldo NO es dinero tuyo hasta que se salda (no se suma a tu dinero disponible).
 */
import { round2 } from '@pf/domain';
import type { TricountRow } from '@pf/workbook';

export interface TricountItem extends TricountRow {
  mine: boolean;
  /** Tu parte del gasto (la mitad). */
  myShare: number;
  /** Tu movimiento, si lo enviaste desde la app (misma fecha e importe). */
  movementId: string;
}
export interface TricountMonth {
  month: string; total: number;
  /** Pagado por ti y por el otro. */
  mine: number; theirs: number;
  /** Tu parte del mes, y la parte tuya de lo que pagó el otro (gasto tuyo que aún no está en tu libro). */
  myShare: number; myShareTheyPaid: number;
}
export interface TricountSummary {
  me: string; other: string;
  /** > 0: el otro te debe; < 0: le debes. */
  balance: number;
  /** Fecha del gasto más antiguo sin saldar (el libro se vacía al saldar); null si no hay. */
  since: string | null;
  /** Saldo al final de cada mes desde el primero sin saldar hasta el actual. */
  curve: Array<{ month: string; balance: number }>;
  items: TricountItem[];
  months: string[];
}

const monthOf = (d: string) => d.slice(0, 7);
const nextMonth = (m: string) => { const [y, mo] = m.split('-').map(Number) as [number, number]; return mo === 12 ? `${y + 1}-01` : `${y}-${String(mo + 1).padStart(2, '0')}`; };

/** El otro participante: el que no eres tú entre los que aparecen (o entre los conocidos). */
export function otherOf(me: string, rows: TricountRow[], known: readonly string[]): string {
  const names = [...new Set([...known, ...rows.map(r => r.payer).filter(Boolean)])];
  return names.find(n => n.toLowerCase() !== me.toLowerCase()) ?? '';
}

/**
 * sent: tus envíos desde la app (fecha e importe → movimiento), para decir «en tu libro».
 */
export function tricountSummary(rows: TricountRow[], me: string, other: string, today: string, sent: Array<{ date: string; amount: number; movementId: string }> = []): TricountSummary {
  const isMe = (p: string) => p.toLowerCase() === me.toLowerCase();
  const used = new Set<string>();
  const items = rows.map((r): TricountItem => {
    const mine = isMe(r.payer);
    const hit = mine ? sent.find(x => !used.has(x.movementId) && x.date === r.date && Math.abs(x.amount - r.amount) < 0.005) : undefined;
    if (hit) used.add(hit.movementId);
    return { ...r, mine, myShare: round2(r.amount / 2), movementId: hit?.movementId ?? '' };
  }).sort((a, b) => b.date.localeCompare(a.date) || b.row - a.row);
  const delta = (x: TricountItem) => (x.mine ? x.amount / 2 : -x.amount / 2);
  const balance = round2(items.reduce((t, x) => t + delta(x), 0));
  const since = items.length ? items[items.length - 1]!.date : null;
  const curve: TricountSummary['curve'] = [];
  if (since) {
    const end = monthOf(today) > monthOf(items[0]!.date) ? monthOf(today) : monthOf(items[0]!.date);
    for (let m = monthOf(since); m <= end; m = nextMonth(m)) {
      curve.push({ month: m, balance: round2(items.filter(x => monthOf(x.date) <= m).reduce((t, x) => t + delta(x), 0)) });
    }
  }
  return { me, other, balance, since, curve, items, months: [...new Set(items.map(x => monthOf(x.date)))] };
}

export function tricountMonth(items: TricountItem[], month: string): TricountMonth {
  const list = items.filter(x => monthOf(x.date) === month);
  const sum = (f: (x: TricountItem) => boolean) => round2(list.filter(f).reduce((t, x) => t + x.amount, 0));
  const mine = sum(x => x.mine), theirs = sum(x => !x.mine);
  return { month, total: round2(mine + theirs), mine, theirs, myShare: round2((mine + theirs) / 2), myShareTheyPaid: round2(theirs / 2) };
}

/** «7 meses», «3 semanas», «5 días» desde una fecha. */
export function ageText(since: string, today: string): string {
  const days = Math.round((Date.parse(today) - Date.parse(since)) / 86400000);
  if (days < 14) return `${days} día${days === 1 ? '' : 's'}`;
  if (days < 60) return `${Math.round(days / 7)} semanas`;
  const months = Math.round(days / 30.44);
  return months < 24 ? `${months} meses` : `${Math.round(months / 12)} años`;
}
